import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  CircularProgress,
  Alert,
  Box,
  Switch,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { RuleEditor } from './RuleEditor';
import { Rule } from './AutoTopUpConfigModal';

type OrganizationOption = {
  id: string;
  name: string;
};

type ClusterOption = {
  id: string;
  name: string;
};

type AssignSimModalProps = {
  open: boolean;
  onClose: (refresh?: boolean) => void;
};

export function AssignSimModal({ open, onClose }: AssignSimModalProps) {
  const { profile } = useAuth();
  const userRole = profile?.role;
  const userOrgId = profile?.organization_id;

  // Form State
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [selectedClusterId, setSelectedClusterId] = useState<string>('');
  const [iccid, setIccid] = useState('');
  const [msisdn, setMsisdn] = useState('');
  const [autoTopUpEnabled, setAutoTopUpEnabled] = useState(false);
  const [rules, setRules] = useState<Rule[]>([]);

  // Dropdown Options State
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
  const [clusters, setClusters] = useState<ClusterOption[]>([]);

  // Control State
  const [loading, setLoading] = useState(false);
  const [fetchingOrgs, setFetchingOrgs] = useState(false);
  const [fetchingClusters, setFetchingClusters] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch Organizations (Superadmin only)
  const fetchOrganizations = useCallback(async () => {
    if (userRole !== 'superadmin') return;
    setFetchingOrgs(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('organizations')
        .select('id, name')
        .order('name');
      if (fetchError) throw fetchError;
      setOrganizations(data || []);
    } catch (err) {
      console.error("Error fetching organizations:", err);
      setError('Failed to load organizations.');
    }
    setFetchingOrgs(false);
  }, [userRole]);

  // Fetch Clusters based on selected Organization
  const fetchClusters = useCallback(async (orgId: string) => {
    if (!orgId) {
      setClusters([]);
      setSelectedClusterId('');
      return;
    }
    setFetchingClusters(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('clusters')
        .select('id, name')
        .eq('organization_id', orgId)
        .order('name');
      if (fetchError) throw fetchError;
      setClusters(data || []);
    } catch (err) {
      console.error("Error fetching clusters:", err);
      setError('Failed to load clusters.');
    }
    setFetchingClusters(false);
  }, []);

  // Initial data fetch or setup based on user role
  useEffect(() => {
    if (open) {
      // Reset form on open
      setIccid('');
      setMsisdn('');
      setAutoTopUpEnabled(false);
      setRules([]);
      setError(null);
      setLoading(false);

      if (userRole === 'superadmin') {
        fetchOrganizations();
        setSelectedOrgId(''); // Superadmin must choose
        setClusters([]); // Clear clusters until org is selected
        setSelectedClusterId('');
      } else if (userOrgId) {
        // Admin/User role - Org is fixed
        setSelectedOrgId(userOrgId);
        fetchClusters(userOrgId);
        setOrganizations([]); // No need to show org dropdown
        setSelectedClusterId(''); // Reset cluster selection
      } else {
         setError("User organization not found.");
      }
    }
  }, [open, userRole, userOrgId, fetchOrganizations, fetchClusters]);

  // Fetch clusters when selected organization changes (for superadmin)
  useEffect(() => {
    if (userRole === 'superadmin' && selectedOrgId) {
      fetchClusters(selectedOrgId);
      setSelectedClusterId(''); // Reset cluster when org changes
    }
  }, [selectedOrgId, userRole, fetchClusters]);

  const handleOrgChange = (event: SelectChangeEvent<string>) => {
    setSelectedOrgId(event.target.value as string);
  };

  const handleClusterChange = (event: SelectChangeEvent<string>) => {
    setSelectedClusterId(event.target.value as string);
  };

  const handleSave = async () => {
    if (!selectedOrgId || !selectedClusterId || !iccid) {
      setError('Organization, Cluster, and ICCID are required.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const simDataToInsert = {
        organization_id: selectedOrgId,
        cluster_id: selectedClusterId,
        iccid: iccid.trim(),
        msisdn: msisdn.trim() || null,
        status: 'active', // Default status
        auto_topup_enabled: autoTopUpEnabled,
        auto_topup_config: autoTopUpEnabled ? rules : null, // Only save rules if enabled
        // Initialize balances? Recommended
        balances: { balance: 0, data: 0, expiry: 0, reward: 0, subscription: 0, reverse: 0 },
        balance_last_checked: null,
        // is_critical_override can default to null
      };

      const { error: insertError } = await supabase
        .from('sim_cards')
        .insert(simDataToInsert);

      if (insertError) {
         if (insertError.code === '23505') { // Handle unique constraint violation (e.g., duplicate ICCID)
            throw new Error('A SIM with this ICCID already exists.');
         } else {
           throw insertError;
         }
      }

      onClose(true); // Close and signal refresh
    } catch (err: any) {
      console.error("Error assigning SIM:", err);
      setError(err.message || 'Failed to assign SIM.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Assign New SIM Card</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box component="form" noValidate sx={{ mt: 1 }}>

           {/* Organization Selector (Superadmin only) */} 
           {userRole === 'superadmin' && (
             <FormControl fullWidth margin="dense" required disabled={fetchingOrgs}>
               <InputLabel id="organization-select-label">Organization</InputLabel>
               <Select
                 labelId="organization-select-label"
                 value={selectedOrgId}
                 label="Organization"
                 onChange={handleOrgChange}
               >
                 <MenuItem value="" disabled><em>Select Organization...</em></MenuItem>
                 {organizations.map((org) => (
                   <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                 ))}
               </Select>
               {fetchingOrgs && <CircularProgress size={20} sx={{ position: 'absolute', right: 40, top: '50%', marginTop: '-10px' }}/>}
             </FormControl>
           )}

          {/* Cluster Selector */} 
          <FormControl fullWidth margin="dense" required disabled={fetchingClusters || !selectedOrgId}>
            <InputLabel id="cluster-select-label">Cluster</InputLabel>
            <Select
              labelId="cluster-select-label"
              value={selectedClusterId}
              label="Cluster"
              onChange={handleClusterChange}
              SelectDisplayProps={{ 'aria-label': 'Select Cluster' }}
            >
               <MenuItem value="" disabled><em>{selectedOrgId ? 'Select Cluster...' : 'Select Organization first'}</em></MenuItem>
               {clusters.map((cluster) => (
                <MenuItem key={cluster.id} value={cluster.id}>{cluster.name}</MenuItem>
              ))}
            </Select>
             {fetchingClusters && <CircularProgress size={20} sx={{ position: 'absolute', right: 40, top: '50%', marginTop: '-10px' }}/>}
          </FormControl>

          {/* ICCID and MSISDN */} 
          <TextField
            required
            fullWidth
            margin="dense"
            id="iccid"
            name="iccid"
            label="ICCID"
            value={iccid}
            onChange={(e) => setIccid(e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            id="msisdn"
            name="msisdn"
            label="MSISDN (Optional)"
            value={msisdn}
            onChange={(e) => setMsisdn(e.target.value)}
          />

          <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 3, pt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Auto Top-Up</Typography>
             {/* Auto Top-Up Toggle */} 
            <FormControlLabel
              control={
                <Switch
                  checked={autoTopUpEnabled}
                  onChange={(e) => setAutoTopUpEnabled(e.target.checked)}
                />
              }
              label="Enable Auto Top-Up for this SIM"
              sx={{ mb: 1 }} 
            />

            {/* Conditionally render RuleEditor */} 
            {autoTopUpEnabled && (
              <RuleEditor rules={rules} onRulesChange={setRules} />
            )}
          </Box>

        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={loading}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={loading || fetchingOrgs || fetchingClusters}
        >
          {loading ? <CircularProgress size={24} /> : 'Assign SIM'}
        </Button>
      </DialogActions>
    </Dialog>
  );
} 